// Dependencies
"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get ExternalReviewAuthor () {
        return ExternalReviewAuthor;
    },
    get RatingScaleOptionModel () {
        return RatingScaleOptionModel;
    },
    get ReviewAuthorParticipant () {
        return ReviewAuthorParticipant;
    },
    get ReviewModel () {
        return ReviewModel;
    },
    get ReviewParticipant () {
        return ReviewParticipant;
    },
    get ReviewQuestionCalculatorAggregationModel () {
        return ReviewQuestionCalculatorAggregationModel;
    },
    get ReviewQuestionModel () {
        return ReviewQuestionModel;
    },
    get ReviewQuestionTypeModel () {
        return ReviewQuestionTypeModel;
    },
    get ReviewReportSnapshotModel () {
        return ReviewReportSnapshotModel;
    },
    get SingleRelativeParticipant () {
        return SingleRelativeParticipant;
    }
});
const _bson = require("bson");
const _zod = require("zod");
const SingleRelativeParticipant = _zod.z.enum([
    "self",
    "manager",
    "manager-manager",
    "other"
]);
const ReviewParticipant = _zod.z.union([
    _zod.z.instanceof(_bson.ObjectId),
    SingleRelativeParticipant,
    _zod.z.literal("contributors")
]);
const ReviewAuthorParticipant = _zod.z.union([
    ReviewParticipant,
    _zod.z.literal("direct-reports")
]);
const ExternalReviewAuthor = _zod.z.object({
    name: _zod.z.string(),
    email: _zod.z.string(),
    "email-body": _zod.z.string()
});
const ReviewModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    subject: _zod.z.instanceof(_bson.ObjectId),
    name: _zod.z.string(),
    start_date: _zod.z.date(),
    creator: _zod.z.instanceof(_bson.ObjectId).nullish(),
    company: _zod.z.instanceof(_bson.ObjectId),
    deleted: _zod.z.boolean().optional(),
    complete_date: _zod.z.date().nullish(),
    workflow_id: _zod.z.instanceof(_bson.ObjectId).nullish(),
    admins_only: _zod.z.boolean().optional(),
    closed: _zod.z.boolean().optional(),
    unassigned_reports: _zod.z.number(),
    // Note, when a review report is deleted it is not always pulled from these array
    reports: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    participants: _zod.z.array(_zod.z.instanceof(_bson.ObjectId)),
    completed: _zod.z.boolean(),
    created_at: _zod.z.date().optional(),
    updated_at: _zod.z.date().optional(),
    updated_by: _zod.z.instanceof(_bson.ObjectId).nullish(),
    deleted_at: _zod.z.date().nullish(),
    deleted_by: _zod.z.instanceof(_bson.ObjectId).nullish(),
    deleted_metadata: _zod.z.object({
        review_cycle_id: _zod.z.instanceof(_bson.ObjectId).nullable(),
        deleted_at: _zod.z.date().nullable()
    }).optional()
});
const ReviewQuestionTypeModel = _zod.z.enum([
    "text",
    "multiline",
    "multiple-select",
    "date",
    "stars",
    "goal-creation",
    "goal-discussion",
    "calculator",
    "rating-scale",
    "multiple-choice",
    "instructions",
    "numeric"
]);
const ReviewQuestionCalculatorAggregationModel = _zod.z.enum([
    "sum",
    "average"
]);
const RatingScaleOptionModel = _zod.z.object({
    value: _zod.z.union([
        _zod.z.number(),
        _zod.z.literal("")
    ]),
    name: _zod.z.string().min(1, {
        message: "This is a required field"
    }).refine((val)=>val.trim() !== "", {
        message: "This is a required field"
    }),
    description: _zod.z.string().optional()
});
const ReviewQuestionModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    company: _zod.z.instanceof(_bson.ObjectId),
    type: ReviewQuestionTypeModel,
    is_hidden: _zod.z.boolean().optional(),
    question: _zod.z.string().max(3000, {
        message: "Question has maximum length of 3000 characters"
    }).default(""),
    /**
	 * For rating scale questions, this is an array of choices.
	 * For multiple choice questions, this is an array of strings.
	 */ choices: _zod.z.union([
        _zod.z.array(_zod.z.string().min(1, {
            message: "Choice cannot be empty"
        })),
        _zod.z.array(RatingScaleOptionModel)
    ]).optional(),
    aggregation: ReviewQuestionCalculatorAggregationModel.optional(),
    questions: _zod.z.array(_zod.z.object({
        id: _zod.z.instanceof(_bson.ObjectId),
        weight: _zod.z.number()
    })).optional(),
    subtext: _zod.z.string().max(12000, {
        message: "Subtitle has maximum length of 12,000 characters"
    }).optional(),
    add_scaling: _zod.z.array(RatingScaleOptionModel).optional(),
    score_weights: _zod.z.boolean().optional(),
    max: _zod.z.number().optional(),
    min: _zod.z.number().optional(),
    decimals: _zod.z.boolean().optional(),
    add_scoring: _zod.z.boolean().optional(),
    "multi-select-minimum": _zod.z.number().optional(),
    "multi-select-maximum": _zod.z.number().optional(),
    scoring_max: _zod.z.number().optional(),
    scoring_min: _zod.z.number().optional(),
    is_required: _zod.z.boolean().optional()
});
const ReviewReportSnapshotModel = _zod.z.object({
    _id: _zod.z.instanceof(_bson.ObjectId),
    action: _zod.z.enum([
        "submit",
        "dump",
        "save"
    ]),
    actor: _zod.z.instanceof(_bson.ObjectId),
    actor_type: _zod.z.enum([
        "author",
        "signer"
    ]),
    answers: _zod.z.array(_zod.z.union([
        _zod.z.string(),
        _zod.z.number(),
        _zod.z.array(_zod.z.string()),
        _zod.z.object({})
    ])),
    company: _zod.z.instanceof(_bson.ObjectId),
    created_at: _zod.z.date(),
    review: _zod.z.instanceof(_bson.ObjectId),
    review_report: _zod.z.instanceof(_bson.ObjectId),
    session: _zod.z.instanceof(_bson.ObjectId)
});
